import { useAuthFetch } from "@/hooks/useAuthFetch";
import { config } from "@/lib/config";
import { toast } from "@/utils/toast";
import { useMutation, useQuery } from "@tanstack/react-query";

// Types for onboarding
export interface OnboardingStatus {
  isOnboarded: boolean;
  defaultTenant?: {
    id: string;
    slug: string;
    name: string;
  } | null;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

export interface OnboardingOptions {
  user: {
    id: string;
    email: string;
    name: string;
  };
  hasExistingOrganizations: boolean;
  suggestedSlug: string;
  suggestedName: string;
}

export interface CreateOrganizationData {
  organizationName: string;
  organizationSlug: string;
  name?: string;
}

export interface CreateOrganizationResponse {
  success: boolean;
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  defaultBookingFlow: {
    id: string;
    slug: string;
    name: string;
  };
}

// Query keys
export const onboardingKeys = {
  status: ["onboarding", "status"] as const,
  options: ["onboarding", "options"] as const,
};

// Hook to check onboarding status
export function useOnboardingStatus() {
  const authFetch = useAuthFetch();

  return useQuery({
    queryKey: onboardingKeys.status,
    queryFn: async (): Promise<OnboardingStatus> => {
      const response = await authFetch(
        `${config.apiBaseUrl}/onboarding/status`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to check onboarding status: ${response.statusText}`
        );
      }

      return await response.json();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Hook to get onboarding options
export function useOnboardingOptions() {
  const authFetch = useAuthFetch();

  return useQuery({
    queryKey: onboardingKeys.options,
    queryFn: async (): Promise<OnboardingOptions> => {
      const response = await authFetch(
        `${config.apiBaseUrl}/onboarding/options`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch onboarding options: ${response.statusText}`
        );
      }

      return await response.json();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Hook to create organization
export function useCreateOrganization() {
  const authFetch = useAuthFetch();

  return useMutation({
    mutationFn: async (
      data: CreateOrganizationData
    ): Promise<CreateOrganizationResponse> => {
      const response = await authFetch(
        `${config.apiBaseUrl}/onboarding/complete`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to create organization: ${response.statusText}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to create organization");
      }

      return result;
    },
    onSuccess: (data) => {
      toast.success(
        `Welcome to ${data.tenant.name}! Your organization has been created.`
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create organization");
    },
  });
}
